import { useMemo } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';

const CONFIG_NAME = 'payer_aware_notes_config';

/**
 * Default payer-aware template IDs
 * These templates are optimized for payer compliance
 */
const DEFAULT_PAYER_AWARE_TEMPLATE_IDS = [
  '6d3d0486-f00f-475f-ab76-80e79413806c',
  'a78747e9-3192-444a-9cc9-a4537f78ff39',
  'c3dcde88-1aae-4ced-bc60-9f36079202a4',
  '1edc455a-9073-4325-9334-d5e628823619',
];

/**
 * Hook to get payer-aware template IDs from Statsig dynamic config.
 *
 * This allows the list of payer-aware templates to be configured remotely
 * without code changes. Used by the JotAudit sidebar to determine whether
 * to show the "regenerate with payer-aware template" banner.
 *
 * Dynamic Config: payer_aware_notes_config
 * Structure: { payer_aware_note_templates: ["template-id-1", "template-id-2", ...] }
 *
 * @returns Array of payer-aware template IDs
 *
 * @example
 * const payerAwareTemplateIds = usePayerAwareTemplatesConfig();
 * const isPayerAware = payerAwareTemplateIds.includes(currentTemplateId);
 */
export function usePayerAwareTemplatesConfig(): string[] {
  const { client } = useStatsigClient();

  return useMemo(() => {
    const dynamicConfig = client.getDynamicConfig(CONFIG_NAME);
    const configValue = dynamicConfig.value as Record<string, unknown> | null;

    // Safely extract payer_aware_note_templates array with type guard
    const payerAwareNoteTemplates =
      configValue && Array.isArray(configValue.payer_aware_note_templates)
        ? (configValue.payer_aware_note_templates as string[])
        : DEFAULT_PAYER_AWARE_TEMPLATE_IDS;

    return payerAwareNoteTemplates;
  }, [client]);
}
